import { useState, useCallback, useEffect } from 'react';
import type { UseResourceCustomContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/types.tsx';
import { useResourceWithCustomRouterContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/use-resource-with-custom-router-context/index.tsx';
import { requiredProjectProperties } from '@atlassian/jira-router-resources-service-desk-project-getting-started/src/services/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { DISABLE_NPLE_PROJECT_PROJECT_KEY, PACKAGE_NAME } from '../../constants.tsx';

export const useGetNpleDisabledProjectProperty = (projectKeyOrId: number | string | undefined) => {
	const [npleDisabled, setNpleDisabled] = useState<boolean | undefined>(
		projectKeyOrId ? undefined : true,
	);
	const [isLoading, setIsLoading] = useState(true);
	const getNpleDisabledProjectProperty = useCallback(async () => {
		try {
			const response = await performGetRequest(
				`/rest/api/3/project/${projectKeyOrId}/properties/${DISABLE_NPLE_PROJECT_PROJECT_KEY}`,
			);
			setNpleDisabled(response.value);
			setIsLoading(false);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setNpleDisabled(error.statusCode !== 404);
			setIsLoading(false);
			error.statusCode !== 404 &&
				fireErrorAnalytics({
					meta: {
						id: 'getDisableNpleProjectProperty',
						packageName: PACKAGE_NAME,
						teamName: 'sequoia',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
		}
	}, [projectKeyOrId]);

	useEffect(() => {
		if (projectKeyOrId) {
			getNpleDisabledProjectProperty();
		}
	}, [getNpleDisabledProjectProperty, projectKeyOrId]);

	return [isLoading, npleDisabled];
};

export const useGetNpleDisabledProjectPropertyWithContext = (
	customContext?: UseResourceCustomContext,
) => {
	const { data: requiredProjectPropertiesData, loading: isGetNpleDisabledLoading } =
		useResourceWithCustomRouterContext(requiredProjectProperties, customContext);

	const npleDisabled = requiredProjectPropertiesData
		? requiredProjectPropertiesData[DISABLE_NPLE_PROJECT_PROJECT_KEY]
		: false;

	return [npleDisabled, isGetNpleDisabledLoading];
};
